import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { Chips } from "primereact/chips";
import LocationPicker from "../../../../components/LocationPicker/LocationPicker";

import "react-toastify/dist/ReactToastify.css";
import "../../crud.css";

const DestinationsForm = ({ settings }) => {
  const [showMap, setShowMap] = useState(false);

  const { t } = useTranslation();

  const isEditMode = settings.formMode === "UPDATE";
  const isDeleteMode = settings.formMode === "DELETE";

  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      active: true,
    },
  });

  const defaultFieldData = {
    name: "",
    paddocks: 0,
    latitude: 0,
    longitude: 0,
    notes: "",
    active: false,
    clientId: 0,
  };

  const formModeConfig = {};

  if (isEditMode) {
    formModeConfig.SubmitButtonText = t("buttons.update");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.updating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.email = data.email.toString();
      await settings.CRUDService.updateEntity(data);
    };
  } else if (isDeleteMode) {
    formModeConfig.SubmitButtonText = t("buttons.delete");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.deleting")}...`;
    formModeConfig.serverAction = async () => {
      await settings.CRUDService.deleteEntity(settings.entityId);
    };
  } else {
    formModeConfig.SubmitButtonText = t("buttons.create");
    formModeConfig.SubmitButtonTextWait = `${t("buttons.creating")}...`;
    formModeConfig.serverAction = async (data) => {
      data.email = data.email.toString();
      await settings.CRUDService.createEntity(data);
    };
  }

  useEffect(() => {
    if (isEditMode || isDeleteMode) {
      const fetchDestination = async () => {
        try {
          const data = await settings.CRUDService.getEntity(settings.entityId);
          data.email = data.email.split(",");

          for (const key in data) {
            setValue(key, data[key]);
          }
        } catch (error) {
          console.error(t("forms.messages.operation.error"), error);
        }
      };

      fetchDestination();
    }
  }, [settings.entityId, setValue, settings.formMode]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      await formModeConfig.serverAction(data);

      settings.hideDialog();

      settings.toast.current.show({
        severity: "success",
        summary: t("forms.messages.titles.success"),
        detail: t("forms.messages.operation.success"),
        life: 3000,
      });
    } catch (error) {
      settings.toast.current.show({
        severity: "error",
        summary: t("forms.messages.titles.error"),
        detail: t("forms.messages.operation.error"),
        life: 3000,
      });

      setIsLoading(false);
    }
  };

  const getFormErrorMessage = (error) => {
    return error && <small className="p-error">{error.message}</small>;
  };

  const hideMap = () => {
    setShowMap(false);
  };

  const getLocation = ({ lat, lng }) => {
    setValue("latitude", lat);
    setValue("longitude", lng);
  };
  const isMail = (value) => {
    let result = true;

    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    value.forEach((element) => {
      result = result && regex.test(element);
    });

    return result;
  };

  return (
    <React.Fragment>
      <div className="flex justify-content-center">
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
          <div className="field">
            <Controller
              name="name"
              control={control}
              rules={{
                required: t("forms.validation.required", {
                  entity: t("entities.destinations.name"),
                }),
                maxLength: {
                  value: 500,
                  message: t("forms.validation.maxLength", {
                    entity: t("entities.destinations.name"),
                    maxLength: 500,
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <React.Fragment>
                  <div className="flex justify-content-between">
                    <label htmlFor="name">
                      {t("entities.destinations.name")}
                    </label>
                    <div>{getFormErrorMessage(errors.name)}</div>
                  </div>
                  <div className="form-crud">
                    <InputText
                      id="name"
                      {...field}
                      value={field.value || ""}
                      maxLength={100}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode === "DELETE"}
                    />
                  </div>
                </React.Fragment>
              )}
            />
          </div>

          <div className="field">
            <Controller
              name="email"
              control={control}
              rules={{
                required: t("forms.validation.required", {
                  entity: t("entities.destinations.email"),
                }),
                maxLength: {
                  value: 500,
                  message: t("forms.validation.maxLength", {
                    entity: t("entities.destinations.email"),
                    maxLength: 500,
                  }),
                },
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Entered value does not match email format",
                },
              }}
              render={({ field, fieldState }) => (
                <React.Fragment>
                  <div className="flex justify-content-between">
                    <label htmlFor="email">
                      {`${t(
                        "entities.destinations.email"
                      )} - (Usar Enter para agregar mails)`}
                    </label>
                    <div>{getFormErrorMessage(errors.email)}</div>
                  </div>
                  <div className="form-crud">
                    <Chips
                      id="email"
                      {...field}
                      value={field.value || ""}
                      maxLength={100}
                      className={[
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode === "DELETE"}
                      onChange={(e) => {
                        if (isMail(e.value)) field.onChange(e.value);
                      }}
                      addOnBlur={true}
                    />
                  </div>
                </React.Fragment>
              )}
            />
          </div>

          {!showMap && (
            <div className="field">
              <Controller
                name="latitude"
                control={control}
                defaultValue={defaultFieldData.latitude}
                rules={{
                  required: t("forms.validation.required", {
                    entity: t("entities.destinations.latitude"),
                  }),
                  min: {
                    value: -90,
                    message: t("forms.validation.min", {
                      entity: t("entities.destinations.latitude"),
                      min: -90,
                    }),
                  },
                  max: {
                    value: 90,
                    message: t("forms.validation.max", {
                      entity: t("entities.destinations.latitude"),
                      max: 90,
                    }),
                  },
                }}
                render={({ field }) => (
                  <React.Fragment>
                    <div className="flex justify-content-between">
                      <label htmlFor="paddocks">
                        {t("entities.destinations.latitude")}
                      </label>
                      <div>{getFormErrorMessage(errors.latitude)}</div>
                    </div>
                    <div className="form-crud">
                      <InputNumber
                        onClick={() => {
                          setShowMap(true);
                        }}
                        id={field.name}
                        value={field.value || 0}
                        onValueChange={(e) => field.onChange(e.value)}
                        mode="decimal"
                        locale="de-DE"
                        minFractionDigits={2}
                        min={-90}
                        max={90}
                        inputClassName={[
                          classNames({ "p-invalid": errors.latitude }),
                        ]}
                        disabled={settings.formMode === "DELETE"}
                      />
                    </div>
                  </React.Fragment>
                )}
              />
            </div>
          )}

          {!showMap && (
            <div className="field">
              <Controller
                name="longitude"
                control={control}
                defaultValue={defaultFieldData.longitude}
                rules={{
                  required: t("forms.validation.required", {
                    entity: t("entities.destinations.longitude"),
                  }),
                  min: {
                    value: -180,
                    message: t("forms.validation.min", {
                      entity: t("entities.destinations.longitude"),
                      min: -180,
                    }),
                  },
                  max: {
                    value: 180,
                    message: t("forms.validation.max", {
                      entity: t("entities.destinations.longitude"),
                      max: 180,
                    }),
                  },
                }}
                render={({ field }) => (
                  <React.Fragment>
                    <div className="flex justify-content-between">
                      <label htmlFor="longitude">
                        {t("entities.destinations.longitude")}
                      </label>
                      <div>{getFormErrorMessage(errors.longitude)}</div>
                    </div>
                    <div className="form-crud">
                      <InputNumber
                        onClick={() => {
                          setShowMap(true);
                        }}
                        id={field.name}
                        value={field.value || 0}
                        onValueChange={(e) => field.onChange(e.value)}
                        mode="decimal"
                        locale="de-DE"
                        minFractionDigits={2}
                        min={-180}
                        max={180}
                        inputClassName={[
                          classNames({ "p-invalid": errors.longitude }),
                        ]}
                        disabled={settings.formMode === "DELETE"}
                      />
                    </div>
                  </React.Fragment>
                )}
              />
            </div>
          )}

          {showMap && (
            <div className="field">
              <LocationPicker hideMap={hideMap} getLocation={getLocation} />
            </div>
          )}

          <div className="field">
            <Controller
              name="notes"
              control={control}
              rules={{
                maxLength: {
                  value: 500,
                  message: t("forms.validation.maxLength", {
                    entity: t("entities.destinations.notes"),
                    maxLength: 500,
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <React.Fragment>
                  <div className="flex justify-content-between">
                    <label htmlFor="notes">
                      {t("entities.destinations.notes")}
                    </label>
                    <div>{getFormErrorMessage(errors.notes)}</div>
                  </div>
                  <div className="form-crud">
                    <InputTextarea
                      id="notes"
                      {...field}
                      value={field.value ? field.value : ""}
                      rows={3}
                      maxLength={500}
                      className={[
                        "form-control",
                        classNames({
                          "p-invalid": fieldState.invalid,
                        }),
                      ]}
                      disabled={settings.formMode === "DELETE"}
                    />
                  </div>
                </React.Fragment>
              )}
            />
          </div>

          <div className="field">
            <div className="field-checkbox">
              <Controller
                name="active"
                control={control}
                render={({ field, fieldState }) => (
                  <Checkbox
                    inputId={field.name}
                    onChange={(e) => field.onChange(e.checked)}
                    checked={field.value}
                    className={classNames({
                      "p-invalid": fieldState.invalid,
                    })}
                    disabled={settings.formMode === "DELETE"}
                  />
                )}
              />
              <label
                htmlFor="accept"
                className={classNames({ "p-error": errors.active })}
              >
                {t("Active")}
              </label>
            </div>
          </div>

          <div className="flex justify-content-between">
            <Button
              label={t("buttons.cancel")}
              icon="pi pi-times"
              className="p-button-text btn-secondary-crud"
              onClick={() => settings.hideDialog("CANCEL")}
              type="button"
              disabled={isLoading}
            />
            <Button
              label={
                isLoading
                  ? formModeConfig.SubmitButtonTextWait
                  : formModeConfig.SubmitButtonText
              }
              icon="pi pi-check"
              className=" btn btn-primary btn-primary-crud"
              type="submit"
              disabled={isLoading}
            />
          </div>
        </form>
      </div>
    </React.Fragment>
  );
};

export default DestinationsForm;
